import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { MainLayout } from '@/components/layout/MainLayout'
import { cn } from '@/lib/utils'
import { BrandProfileTab } from '@/components/settings/BrandProfileTab'
import { AccountTab } from '@/components/settings/AccountTab'
import { TeamTab } from '@/components/settings/TeamTab'
import { BrandsTab } from '@/components/settings/BrandsTab'
import { NotificationsTab } from '@/components/settings/NotificationsTab'
import { AppTab } from '@/components/settings/AppTab'
import { ToolInterest } from '@/components/settings/ToolInterest'

const TABS = [
  { id: 'brand', label: 'Brand Profile' },
  { id: 'account', label: 'Account' },
  { id: 'team', label: 'Team' },
  { id: 'brands', label: 'Brands' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'app', label: 'App' },
  { id: 'tools', label: 'Tools' },
] as const
type TabId = (typeof TABS)[number]['id']

/** Settings: the company, its brands and team, this person's account and preferences */
const Settings: React.FC = () => {
  const [params, setParams] = useSearchParams()
  const requested = params.get('tab')
  const tab: TabId = TABS.some((t) => t.id === requested) ? (requested as TabId) : 'brand'
  const go = (id: TabId) => setParams({ tab: id })

  return (
    <MainLayout>
      <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Settings</h1>
          <p className="mt-1 text-muted-foreground">
            Manage your company, brand, team, and preferences.
          </p>
        </div>

        <nav
          role="tablist"
          aria-label="Settings"
          className="flex flex-wrap gap-1 rounded-2xl border border-border bg-card p-1.5 shadow-sm"
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => go(t.id)}
              className={cn(
                'h-10 flex-1 whitespace-nowrap rounded-xl px-4 text-sm font-semibold transition-colors',
                tab === t.id
                  ? 'bg-[#C0FD72] text-[#0F172A] shadow-sm'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div role="tabpanel">
          {tab === 'brand' ? <BrandProfileTab onAddBrand={() => go('brands')} /> : null}
          {tab === 'account' ? <AccountTab /> : null}
          {tab === 'team' ? <TeamTab /> : null}
          {tab === 'brands' ? <BrandsTab /> : null}
          {tab === 'notifications' ? <NotificationsTab /> : null}
          {tab === 'app' ? <AppTab /> : null}
          {tab === 'tools' ? (
            <div className="max-w-3xl rounded-2xl border border-border bg-card p-5 shadow-sm">
              <ToolInterest />
            </div>
          ) : null}
        </div>
      </div>
    </MainLayout>
  )
}

export default Settings
